/**
 * What each role may do, in one table.
 *
 * ---------------------------------------------------------------------------
 * Read this before you use it as a security boundary, because it is not one
 * ---------------------------------------------------------------------------
 *
 * Invariant 1 still holds: the boundary is Postgres row-level security. This table
 * is a SECOND, NARROWER gate on top of it, and it only ever subtracts. Granting a
 * capability here grants nothing — an ACCOUNTING actor with `inventory:read` still
 * sees exactly the rows `properties_staff_all` lets them see, and an OWNER with
 * `tenants:read` still sees only tenants in units they currently hold.
 *
 * It exists because RLS is deliberately coarse about staff. Almost every staff
 * policy is `app_is_staff()` — one predicate for four roles — and the one place the
 * database does distinguish them is `app_can_read_sensitive()`. That coarseness is
 * the right call for policies (four near-identical policies per table is four
 * places to get a join wrong), but it leaves the product question unanswered: a
 * maintenance tech can technically UPDATE a property's parcel number, and nobody
 * wants them to.
 *
 * So role separation for staff is app-layer. The consequence, stated plainly: a
 * staff account that reaches the database directly, or a bug that bypasses a
 * preHandler, is limited by RLS and not by this file. If one of these separations
 * ever needs to survive that — an auditor requiring that ACCOUNTING provably cannot
 * mutate inventory — it needs a policy, not a row in this table.
 *
 * ---------------------------------------------------------------------------
 * The one entry that must not drift
 * ---------------------------------------------------------------------------
 *
 * `sensitive:read` / `sensitive:write` mirror `app_can_read_sensitive()` in
 * rls-policies.sql: ADMIN, PROPERTY_MANAGER, ACCOUNTING. Those two must agree.
 * If they disagree in the safe direction the UI hides a panel the database would
 * have filled; in the unsafe direction the UI shows a panel that renders an empty
 * record, which reads as "nothing on file" for an SSN that exists. `roles.test.ts`
 * pins the list; the RLS suite proves what the database actually does.
 */
import { ACTOR_TYPES, STAFF_ROLES } from './enums.js';
export const CAPABILITIES = [
    /** Properties, units, ownerships — reading them. Every actor has this; RLS decides the rows. */
    'inventory:read',
    /** Creating, editing and archiving properties, units and ownerships. */
    'inventory:write',
    /** Enumerating owners and reading an owner record. */
    'owners:read',
    'owners:write',
    /** Reading tenant contact records. Owners have it, scoped to their own units. */
    'tenants:read',
    'tenants:write',
    /**
     * Manually resetting a tenant's portal password to a server-generated one
     * that staff see once and hand to the tenant (`manuallyResetTenantPassword`,
     * modules/tenants/service.ts). ADMIN and DEVELOPER only — both inherit it
     * from their "everything except..." definitions below; no hand-listed role
     * includes it. Narrower than `tenants:write` on purpose: the person
     * performing it learns a working credential for someone else's account.
     * Capped at 4 per tenant per calendar month by `tenant_password_resets`.
     */
    'tenants:reset-password',
    /**
     * Leases, lease_tenants, checklists, and notices — one pair for all four
     * tables. Same principle as the rest of this file: RLS already scopes every
     * one of them per actor, this only decides whether the console offers the
     * button.
     */
    'leases:read',
    'leases:write',
    /** The encrypted tables. Mirrors app_can_read_sensitive() — see the note above. */
    'sensitive:read',
    'sensitive:write',
    'documents:read',
    /** Uploading and deleting files. Maintenance has it for inspection photos. */
    'documents:write',
    /** Charges, payments, balances — reading them. Every staff role and both portal actors. */
    'ledger:read',
    /**
     * Recording a manual payment, allocating it, reversing it, voiding a charge.
     * ADMIN and ACCOUNTING only — matches the file's own stated separation: the
     * two roles that mutate inventory are not the two that touch money.
     */
    'ledger:write',
    /**
     * Vacancy, lease expirations, deposits held, deposits collected by
     * settlement date, compliance clocks, AR aging. ADMIN, PROPERTY_MANAGER,
     * ACCOUNTING — not MAINTENANCE, same line CLAUDE.md draws for these:
     * financial reports are not for that role.
     */
    'reports:read',
    /**
     * Vendors and work orders. ADMIN, PROPERTY_MANAGER, and MAINTENANCE — the
     * three roles that touch the physical portfolio. Not ACCOUNTING: vendor
     * cost isn't wired to the ledger yet, so there's nothing there for that
     * role to do.
     */
    'maintenance:read',
    'maintenance:write',
    /**
     * Setting or editing a work order's `cost` figure. ADMIN and
     * PROPERTY_MANAGER only — narrower than `maintenance:write`, which
     * MAINTENANCE keeps (status, vendor, notes). Same "narrow capability per
     * sensitive write action" pattern as `screening-criteria:write` /
     * `document-templates:write` / `owner-disbursements:approve`: this dollar
     * figure feeds the maintenance-by-vendor/by-property reports
     * (`reports:read`, which MAINTENANCE lacks), so it isn't the technician's
     * to record. `maintenance:read` still covers *seeing* this value.
     */
    'maintenance:write-cost',
    /**
     * Creating a maintenance request for a unit the tenant leases. TENANT
     * only, deliberately separate from `maintenance:write` rather than
     * reused — same reasoning `payments:pay` already established for a
     * portal-initiated write: a narrow, portal-scoped capability per write
     * action rather than widening the staff one, which would also open the
     * status/vendor/note endpoints it gates (RLS would block those, since
     * there is no tenant UPDATE policy on work_orders, but the capability
     * itself should not pretend to grant them).
     */
    'maintenance:submit',
    /**
     * Applications, screening results, and decisions. ADMIN and
     * PROPERTY_MANAGER on the staff side; the APPLICANT portal actor also
     * carries these for their own RLS-scoped writes (see
     * `applications_applicant_*` in rls-policies.sql — this capability decides
     * whether a route is even attempted, RLS decides which rows it can touch).
     * Not ACCOUNTING or MAINTENANCE: screening decisions aren't either role's
     * job.
     */
    'applications:read',
    'applications:write',
    /**
     * Publishing a new version of a screening criterion. ADMIN only, narrower
     * than `applications:write` — same reasoning as `import:write`: the
     * published criteria are what a decline decision points at for the life of
     * every application decided against that version, so who can add one is
     * kept to the one account, not opened to every role that can decide an
     * application.
     */
    'screening-criteria:write',
    /** The audit trail. ADMIN only — it is the record of what everyone else did. */
    'audit:read',
    /**
     * Linking a bank account and submitting an ACH rent payment through the
     * portal. TENANT only — the portal's first WRITE capability. Not staff:
     * they record check/cash/money-order payments through `ledger:write`
     * instead, never on a tenant's behalf through Stripe. Not OWNER: owners
     * have no reason to move money into a lease.
     */
    'payments:pay',
    /**
     * A tenant recording their own SMS consent decision from the portal
     * popup (TenantSmsConsentPrompt.tsx). TENANT only, same "portal's own
     * write" shape as `payments:pay` above — not staff, who record it from
     * the tenant's profile through `messaging:write` instead; not OWNER or
     * VENDOR, who have no SMS consent of their own to record.
     */
    'sms-consent:manage',
    /**
     * Onboarding owner and tenant contact records from Buildium or a CSV, or
     * typing several in by hand. ADMIN only, not aliased to `owners:write` /
     * `tenants:write`: there is exactly one admin account (invariant 1), and
     * bulk-seeding contact records — soon to be the entry point for a Buildium
     * API key and CSV uploads — is deliberately kept to that one account rather
     * than opened to every role that can already create one owner at a time.
     */
    'import:write',
    /**
     * Editing portfolio-wide settings (currently just the public contact
     * email). ADMIN only, same narrow-grant reasoning as `import:write` and
     * `screening-criteria:write` — this is a single account's call, not
     * opened to every role that happens to touch the affected page.
     */
    'settings:write',
    /**
     * Publishing a new version of a notice or lease-agreement document
     * template. ADMIN only, same narrow-grant reasoning as
     * `screening-criteria:write` and `import:write`: a published template is
     * what every generated Document points at for the life of that
     * document's history (Document.documentTemplateId), so who can add one is
     * kept to the one account.
     */
    'document-templates:write',
    /**
     * Editing the body content of the four public compliance/policy pages
     * (compliance, privacy policy, terms of use, equal housing). ADMIN and
     * DEVELOPER — deliberately not narrowed to ADMIN-only like
     * `settings:write`/`document-templates:write`/`screening-criteria:write`,
     * since there is no "one account's call" reasoning here: this is ordinary
     * firm-facing legal copy, not a financial approval or a document version
     * every future generated record points at.
     */
    'compliance-pages:write',
    /**
     * Approving a per-owner disbursement — the row that gets created before
     * any Stripe call — or recording an external settlement against one.
     * ADMIN only, no "approve all," per the firm's explicit request. This is
     * now the only disbursement-approval capability in the app — an earlier,
     * separate `disbursements:approve` flag (a `Payment`-level visibility gate
     * that never moved money, predating this real Connect pipeline) was
     * retired once the owner portal had a real status to show instead.
     */
    'owner-disbursements:approve',
    /**
     * Owner-initiated self-enrollment in Stripe Connect — minting a fresh
     * Account Link and starting or resuming onboarding. OWNER only, and the
     * portal's first WRITE capability: every OWNER capability before this one
     * only ever decided whether a nav link rendered over rows RLS already
     * scoped read-only. Same kind of deliberate departure the APPLICANT actor
     * made in step 10 ("the applicant actor breaks a rule on purpose") — flagged
     * here rather than silently added. It stays narrow on purpose: no RLS write
     * policy on "owners" is needed for it, because the enrollment service reads
     * via the owner's own owners_self_select-scoped `db` to authorize and
     * writes via systemPrisma, the same startEnrollment/confirmEnrollment shape
     * stripe-service.ts already uses for tenants.
     */
    'owner-connect:enroll',
    /**
     * Approving a vendor payout for one or more completed work orders — the
     * row that gets created before any Stripe call — or recording an external
     * settlement against one. ADMIN only, same "no approve all" posture as
     * `owner-disbursements:approve`: this moves money, and the firm's rule
     * applies here too even though it was never asked about vendors
     * specifically — the reasoning (a deliberate, reviewed action per payout)
     * doesn't depend on which payee it is.
     */
    'vendor-disbursements:approve',
    /**
     * Vendor-initiated self-enrollment in Stripe Connect — minting a fresh
     * Account Link and starting or resuming onboarding. VENDOR only, same
     * shape as `owner-connect:enroll`: no RLS write policy on "vendors" is
     * needed for it, because the enrollment service reads via the vendor's
     * own vendors_self_select-scoped `db` to authorize and writes via
     * systemPrisma.
     */
    'vendor-connect:enroll',
    /**
     * Texting a tenant and viewing the resulting thread — the SMS messaging
     * panel and the tenant profile's "Text" button. ADMIN and
     * PROPERTY_MANAGER only, the firm's explicit choice: not ACCOUNTING, not
     * MAINTENANCE, even though either could plausibly want to reach a tenant
     * — kept narrow rather than defaulted open, same posture every other
     * capability in this file takes toward staff separation.
     */
    'messaging:read',
    'messaging:write',
    /**
     * The staff console's "Developer" menu (currently just Test Emails, a
     * thin wrapper around the document-templates preview/send-test-email
     * endpoints for previewing and test-sending the two transactional-email
     * template kinds). DEVELOPER only — deliberately NOT ADMIN, unlike every
     * other capability in this file, which is why this is the one entry
     * `STAFF_ROLE_CAPABILITIES.ADMIN` below explicitly filters back out
     * rather than inheriting via the bare `CAPABILITIES` alias.
     *
     * This does not narrow the underlying `document-templates:write` routes
     * those tools call — ADMIN still needs those for the real Document
     * Templates composer's own live preview and test-send buttons
     * (`DocumentTemplateList.tsx`), which hit the identical endpoints. So an
     * ADMIN who navigates straight to /staff/developer/test-emails can still
     * use it once there, same as any other nav-hidden route in this app
     * (StaffLayout.tsx's own "hiding is not protecting" doc comment) — this
     * capability's job is only to keep the menu entry out of ADMIN's nav,
     * not to create a new backend boundary between two roles that
     * legitimately share the same underlying action.
     */
    'developer:access',
];
/**
 * Staff roles.
 *
 * The shape worth noticing is that the two roles that can mutate inventory (ADMIN,
 * PROPERTY_MANAGER) are not the two that work with money (ADMIN, ACCOUNTING). That
 * separation is the point of having roles at all — an accountant who can silently
 * re-point a unit to a different property can move money without leaving a ledger
 * entry.
 *
 * Correction: this used to say "no role except ADMIN has every capability," true
 * back when ADMIN was a bare alias for the whole `CAPABILITIES` array. `developer:access`
 * broke that on purpose — see its own doc comment above — so ADMIN is now that array
 * minus the one entry reserved for DEVELOPER, and DEVELOPER is the one role that can
 * have something ADMIN lacks.
 */
const STAFF_ROLE_CAPABILITIES = {
    ADMIN: CAPABILITIES.filter((capability) => capability !== 'developer:access'),
    PROPERTY_MANAGER: [
        'inventory:read',
        'inventory:write',
        'owners:read',
        'owners:write',
        'tenants:read',
        'tenants:write',
        'leases:read',
        'leases:write',
        'sensitive:read',
        'sensitive:write',
        'documents:read',
        'documents:write',
        'ledger:read',
        'maintenance:read',
        'maintenance:write',
        'maintenance:write-cost',
        'reports:read',
        'applications:read',
        'applications:write',
        'messaging:read',
        'messaging:write',
    ],
    // Reads inventory, does not shape it. Sensitive access is for tax IDs on 1099s,
    // which is the same business need the database recognises. Reads leases for
    // billing context (rent, due day, deposit) but does not author them — that stays
    // with the two roles that shape the portfolio. `ledger:write` is the point of
    // this role existing at all.
    ACCOUNTING: [
        'inventory:read',
        'owners:read',
        'tenants:read',
        'leases:read',
        'sensitive:read',
        'sensitive:write',
        'documents:read',
        'documents:write',
        'ledger:read',
        'ledger:write',
        'reports:read',
    ],
    // The narrowest staff role. No owner records, no SSNs, no money, no lease terms —
    // a work order needs an address, a unit, and a name, which is exactly
    // `maintenance:*` plus what was already here. `documents:write` is for
    // inspection photos, which is the other thing this role produces.
    MAINTENANCE: [
        'inventory:read',
        'tenants:read',
        'documents:read',
        'documents:write',
        'maintenance:read',
        'maintenance:write',
    ],
    // Everything ADMIN has, minus the two things a deployment/technical
    // account should not be able to do: approve or push an owner
    // disbursement (real money leaving the platform), and read the encrypted
    // sensitive tables (SSNs, tax IDs — no legitimate need for a technical
    // role, so narrower by default here even though the rest of this role
    // mirrors ADMIN). A filter rather than a hand-listed array, so a future
    // capability added to CAPABILITIES lands here automatically, same as it
    // does for ADMIN's filtered alias above, unless explicitly excluded
    // below. Not excluded here, deliberately: `developer:access`, which is
    // how this role ends up with the one thing ADMIN's own filter carves out
    // — the "Developer" nav menu is meant for this role specifically.
    DEVELOPER: CAPABILITIES.filter((capability) => capability !== 'owner-disbursements:approve' &&
        capability !== 'vendor-disbursements:approve' &&
        capability !== 'sensitive:read' &&
        capability !== 'sensitive:write'),
};
/**
 * Portal actors.
 *
 * Mostly read-only: every list they can reach is already scoped by a policy, so
 * most of these capabilities only decide whether a nav link renders. An owner
 * gets `tenants:read` because "who is in my unit" is a legitimate owner question
 * that `tenants_owner_select` already answers; a tenant does not, because tenants
 * do not see their co-tenants' contact records.
 *
 * Neither gets `sensitive:read` at any level. An owner cannot read their own tax ID
 * through the portal — there is no `owner_sensitive` policy for them to read it
 * through, and this table agrees rather than promising something the database will
 * refuse.
 *
 * TENANT carries `documents:write` in addition to `maintenance:submit` — the
 * same blanket grant APPLICANT already has, since RLS (`documents_tenant_insert`/
 * `_update` in rls-policies.sql, scoped through `app_tenant_owns_work_order`) is
 * the actual gate on which parent a tenant may attach a file to, not this table.
 */
const PORTAL_CAPABILITIES = {
    OWNER: [
        'inventory:read',
        'tenants:read',
        'leases:read',
        'documents:read',
        'ledger:read',
        'owner-connect:enroll',
    ],
    TENANT: [
        'inventory:read',
        'leases:read',
        'documents:read',
        'documents:write',
        'ledger:read',
        'payments:pay',
        'maintenance:submit',
        'sms-consent:manage',
    ],
    // The applicant's own writes are enforced by RLS (`applications_applicant_*`
    // / `documents_applicant_*` in rls-policies.sql), scoped to their own
    // application and gated on it still being DRAFT — these capabilities only
    // decide whether the route is attempted at all, same as every other entry
    // in this table.
    APPLICANT: ['applications:read', 'applications:write', 'documents:read', 'documents:write'],
    // Public routes (GET /api/listings) carry no preHandler/capability check
    // at all — same posture as GET /api/pre-qualification-criteria. This
    // entry exists only to satisfy PORTAL_CAPABILITIES' Record type once
    // PUBLIC joined ActorType; capabilitiesFor('PUBLIC') should never
    // actually be called.
    PUBLIC: [],
    // A vendor's own portal (step 14). GET /api/work-orders, GET /api/vendors,
    // and GET /api/vendor-disbursements are all `app.authenticate` only (no
    // capability gate) — RLS alone scopes a vendor to their own rows, same as
    // every other read in this app. The only capability a vendor needs is the
    // one WRITE every other Connect-enrolling actor gets.
    VENDOR: ['vendor-connect:enroll'],
};
/**
 * The capabilities of an actor.
 *
 * A STAFF actor with no role gets nothing rather than a default. `actorSchema`
 * already rejects that shape, so this branch is unreachable through the API — but
 * defaulting a roleless staff member to PROPERTY_MANAGER "to be helpful" is how a
 * half-migrated user row becomes a privilege grant.
 */
export function capabilitiesFor(actorType, staffRole) {
    if (actorType === 'STAFF') {
        return staffRole ? STAFF_ROLE_CAPABILITIES[staffRole] : [];
    }
    return PORTAL_CAPABILITIES[actorType];
}
export function hasCapability(capabilities, capability) {
    return capabilities?.includes(capability) ?? false;
}
/** Every role that can do something, for tests and for error messages. */
export function rolesWith(capability) {
    return STAFF_ROLES.filter((role) => STAFF_ROLE_CAPABILITIES[role].includes(capability));
}
/** Human-readable, for the "your role cannot do this" copy. */
export function describeRole(actorType, staffRole) {
    if (actorType === 'OWNER')
        return 'Owner';
    if (actorType === 'APPLICANT')
        return 'Applicant';
    if (actorType === 'VENDOR')
        return 'Vendor';
    if (actorType !== 'STAFF')
        return 'Resident';
    switch (staffRole) {
        case 'ADMIN':
            return 'Administrator';
        case 'PROPERTY_MANAGER':
            return 'Property manager';
        case 'ACCOUNTING':
            return 'Accounting';
        case 'MAINTENANCE':
            return 'Maintenance';
        case 'DEVELOPER':
            return 'Developer';
        default:
            return 'Staff';
    }
}
/** Guard for the exhaustive-check in tests. */
export const ALL_ACTOR_TYPES = ACTOR_TYPES;
//# sourceMappingURL=roles.js.map